import { isCapacitorNative } from 'Utils/Constants';

/**
 * MUS-630: the web app is one page cached for an hour, and signing in happens inside it, so a tab
 * opened before a release can go on selling from the build it loaded. Before the paywall shows,
 * and after an in-page sign-in, the page compares the entry script it runs with the one the site
 * serves now and reloads once when they differ. The store apps carry their own build: never there.
 */

/** The build this tab has already reloaded for; a reload that did not cure a mismatch is final. */
export const RELOADED_FOR_BUILD_KEY = 'museflow.reloadedForBuild';
const ENTRY_SCRIPT = /\/assets\/(index-[\w-]+\.js)$/;

/** The hashed entry script among a page's module scripts, by file name; null on a dev server. */
function entryScriptOf(scripts: ArrayLike<HTMLScriptElement>, base: string): string | null {
  const names = Array.from(scripts)
    .filter((script) => script.type === 'module' && script.getAttribute('src'))
    .map((script) => new URL(script.getAttribute('src')!, base).pathname.match(ENTRY_SCRIPT)?.[1])
    .filter((name): name is string => !!name);
  return names.length === 1 ? names[0] : null;
}

/** Whether this page could ever be told about a newer build: web, and a built page. */
export const canCheckForNewerBuild = () =>
  !isCapacitorNative() && entryScriptOf(document.scripts, location.href) !== null;

async function servedEntryScript(): Promise<string | null> {
  const response = await fetch('/index.html', { cache: 'no-store' });
  if (!response.ok) return null;
  const page = new DOMParser().parseFromString(await response.text(), 'text/html');
  return entryScriptOf(page.scripts, location.href);
}

function claimReloadFor(build: string): boolean {
  try {
    if (sessionStorage.getItem(RELOADED_FOR_BUILD_KEY) === build) return false;
    sessionStorage.setItem(RELOADED_FOR_BUILD_KEY, build);
    return true;
  } catch {
    // Without storage a reload could not be kept from repeating.
    return false;
  }
}

interface ReloadOptions {
  /** Asked once a newer build is confirmed; false when the page is in use and must not reload. */
  mayReload?: () => boolean;
  reloadPage?: () => void;
}

/**
 * Reloads the page when the site serves a newer build than the one running. True when it reloads.
 * Any doubt (no answer, a page it cannot read, a dev server, the store apps) means no reload.
 */
export async function reloadIfNewerBuild({
  mayReload = () => true,
  reloadPage = () => window.location.reload(),
}: ReloadOptions = {}): Promise<boolean> {
  if (!canCheckForNewerBuild()) return false;
  const running = entryScriptOf(document.scripts, location.href);
  try {
    const served = await servedEntryScript();
    if (!served || served === running || !mayReload() || !claimReloadFor(served)) return false;
    reloadPage();
    return true;
  } catch {
    return false;
  }
}
